import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { XCircle } from 'lucide-react'
import { useCancelNegotiation } from '../../hooks/useNegotiations'

interface CancelNegotiationButtonProps {
  negotiationId: number
  actorId?: string
  ownerActorId?: string
  onCancelled?: () => void
}

export function CancelNegotiationButton({
  negotiationId,
  actorId,
  ownerActorId,
  onCancelled,
}: CancelNegotiationButtonProps) {
  const { t } = useTranslation()
  const cancelNegotiation = useCancelNegotiation()
  const [showConfirm, setShowConfirm] = useState(false)

  // Only owner can cancel
  if (!actorId || actorId !== ownerActorId) return null

  const handleConfirm = async () => {
    setShowConfirm(false)
    try {
      await cancelNegotiation.mutateAsync({ negotiationId })
      onCancelled?.()
    } catch {
      // Error handled by mutation state
    }
  }

  if (showConfirm) {
    return (
      <div className="px-3 py-2 border-t border-neutral-100 dark:border-neutral-800 space-y-2">
        <p className="text-[10px] text-neutral-600 dark:text-neutral-400">
          {t('negotiate.cancelConfirm')}
        </p>
        <div className="flex gap-1.5">
          <button
            onClick={() => setShowConfirm(false)}
            className="flex-1 px-2 py-1.5 text-[10px] font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700 rounded transition-colors"
          >
            {t('negotiate.conflict.dismiss')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={cancelNegotiation.isPending}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-white bg-error-500 hover:bg-error-600 disabled:opacity-50 rounded transition-all duration-200"
          >
            <XCircle size={14} />
            {t('negotiate.cancelNegotiation')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-3 py-1">
      <button
        onClick={() => setShowConfirm(true)}
        disabled={cancelNegotiation.isPending}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-neutral-500 hover:text-error-600 hover:bg-error-50 dark:text-neutral-400 dark:hover:text-error-400 dark:hover:bg-error-900/20 rounded-lg transition-colors disabled:opacity-50"
      >
        <XCircle size={13} />
        {t('negotiate.cancelNegotiation')}
      </button>
    </div>
  )
}
